/**
 * Applies a map's one fit target (see `mapFitTarget.ts`) in the frame its
 * container first has a size and on every container resize until the owner
 * first touches the map or its controls. No timers.
 */
import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { applyMapFit, type MapFitTarget } from '@/components/Map/mapFitTarget';

const MAP_GESTURES = ['pointerdown', 'wheel', 'keydown', 'touchstart'] as const;

export const MapFitController: React.FC<{ target: MapFitTarget | null }> = ({ target }) => {
  const map = useMap();
  const latest = useRef(target);
  latest.current = target;
  const key = target?.key ?? null;

  useEffect(() => {
    if (key === null || typeof ResizeObserver === 'undefined') return undefined;
    const container = map.getContainer();
    // The map's own overlay controls sit beside the canvas in the same host.
    const gestureRoot = container.parentElement ?? container;
    let retained = true;
    const observer = new ResizeObserver(() => {
      const current = latest.current;
      if (retained && current) applyMapFit(map, current);
      else map.invalidateSize(false);
    });
    observer.observe(container);
    const release = () => {
      retained = false;
    };
    MAP_GESTURES.forEach((name) =>
      gestureRoot.addEventListener(name, release, { capture: true, passive: true }),
    );
    return () => {
      observer.disconnect();
      MAP_GESTURES.forEach((name) =>
        gestureRoot.removeEventListener(name, release, { capture: true }),
      );
    };
  }, [key, map]);

  return null;
};
